var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { memo, useEffect, useMemo, useState } from 'react';
import Image from 'next/image';
import Link from 'next/link';
import { routeUrls } from 'root/src/shared/descriptions/routes/routeUrls';
import { DropQueryHash } from 'root/src/client/v2/_pages/drops/fixtures/drops.constants';
import { ModalLayout } from 'root/src/client/v2/common/layouts/ModalLayout';
import { ButtonNew, ButtonNewVariant } from 'root/src/client/v2/common/components/form/ButtonNew';
import { useAcceptDare } from 'root/src/client/v2/drop/hooks/useAcceptDare';
import { useModal } from 'root/src/client/v2/common/contexts/modal.context';
import { buildAssetUrl, assetSize } from 'root/src/client/v2/common/helpers/assetUrl';
import { DarePlatformIcon, PlatformIconColor } from 'root/src/client/web/base/CustomButton/PlatformIcon';
import getBountyName from 'root/src/shared/util/getBountyName';
import { FaArrowRight, FaArrowUpRightFromSquare, FaBolt, FaCaretRight, FaGift, FaRegClock, } from 'react-icons/fa6';
import { dropDareDifficultyEstimatedTime, readableDropDareDifficultyLevels, } from 'root/src/shared/constants/dropEvent';
const STAMP_ROTATIONS = ['-rotate-2', 'rotate-1', '-rotate-1', 'rotate-2'];
const KEYFRAMES_CSS = `
@keyframes sd-marquee {
	0% { transform: translateX(0); }
	100% { transform: translateX(-50%); }
}
@keyframes sd-wiggle {
	0%, 100% { transform: rotate(-4deg); }
	50% { transform: rotate(-1deg); }
}
@keyframes sd-nudge {
	0%, 100% { transform: translateX(0); }
	50% { transform: translateX(6px); }
}
@keyframes sd-twinkle {
	0%, 100% { transform: scale(1); opacity: 0.4; }
	50% { transform: scale(1.4); opacity: 1; }
}
@keyframes sd-drift-a {
	0% { transform: translate(-25%, -30%) scale(1); }
	100% { transform: translate(25%, 35%) scale(1.15); }
}
@keyframes sd-drift-b {
	0% { transform: translate(40%, 30%) scale(1.1); }
	100% { transform: translate(-30%, -20%) scale(1); }
}
@keyframes sd-drift-c {
	0% { transform: translate(-50%, 20%) scale(1); }
	100% { transform: translate(40%, -40%) scale(1.2); }
}
@media (prefers-reduced-motion: reduce) {
	[data-sd-anim] { animation: none !important; }
}
.sd-modal > button[title="Close modal"] {
	background: var(--pink);
	border: none;
	border-radius: 9999px;
	padding: 0;
	width: 2.25rem;
	height: 2.25rem;
	top: 0.25rem;
	right: 0.5rem;
	z-index: 4;
	display: flex;
	align-items: center;
	justify-content: center;
}
.sd-modal > button[title="Close modal"]:hover {
	opacity: 0.85;
}
.sd-modal > button[title="Close modal"] svg {
	color: #ffffff;
	fill: #ffffff;
	width: 1.15rem;
	height: 1.15rem;
}
`;
const SuggestedDareKeyframes = () => <style dangerouslySetInnerHTML={{ __html: KEYFRAMES_CSS }}/>;
const CAROUSEL_INTERVAL_MS = 3500;
const AssetCarousel = memo(({ images, alt }) => {
    const [activeIndex, setActiveIndex] = useState(0);
    useEffect(() => {
        if (images.length <= 1) {
            return undefined;
        }
        const id = window.setInterval(() => {
            setActiveIndex(i => (i + 1) % images.length);
        }, CAROUSEL_INTERVAL_MS);
        return () => window.clearInterval(id);
    }, [images.length]);
    if (!images.length) {
        return null;
    }
    return (<div className="absolute inset-0 overflow-hidden">
			{images.map((src, i) => (<div key={src} className="absolute inset-0 transition-opacity duration-[900ms] ease-in-out" style={{ opacity: i === activeIndex ? 1 : 0 }}>
					<img src={src} alt={i === 0 ? alt : ''} className="w-full h-full object-cover"/>
				</div>))}
			{images.length > 1 && (<div className="absolute bottom-1.5 left-1/2 -translate-x-1/2 flex gap-1 z-[1]">
					{images.map((src, i) => (<span key={src} className={`block w-1.5 h-1.5 rounded-full border border-dark-1 transition-colors ${i === activeIndex ? 'bg-teal' : 'bg-white/60'}`}/>))}
				</div>)}
		</div>);
});
const upgradeIgdbImageUrl = (url) => {
    if (!url) {
        return url;
    }
    if (url.includes('images.igdb.com')) {
        return url.replace(/\/t_[a-z0-9_]+\//i, '/t_720p/');
    }
    if (url.includes('static-cdn.jtvnw.net') && /IGDB-\d+x\d+\./i.test(url)) {
        return url.replace(/IGDB-\d+x\d+\.(jpg|jpeg|png|webp)/i, 'IGDB-285x380.$1');
    }
    return url;
};
const getCarouselImages = (suggestion) => {
    var _a, _b, _c, _d, _e, _f, _g;
    const assetSources = [];
    for (const asset of (_a = suggestion.drop.assets) !== null && _a !== void 0 ? _a : []) {
        const stored = asset.desktop || asset.mobile;
        const url = buildAssetUrl(stored, { w: assetSize('mobile'), fmt: 'auto' });
        if (url) {
            assetSources.push(url);
        }
    }
    const dropForGame = suggestion.drop;
    const fallbackSources = [];
    if (suggestion.serializedDrop.image) {
        fallbackSources.push(upgradeIgdbImageUrl(suggestion.serializedDrop.image));
    }
    if ((_b = dropForGame.gameObj) === null || _b === void 0 ? void 0 : _b.image) {
        fallbackSources.push(upgradeIgdbImageUrl(dropForGame.gameObj.image));
    }
    for (const game of (_c = suggestion.drop.similarGames) !== null && _c !== void 0 ? _c : []) {
        if (game.image) {
            fallbackSources.push(upgradeIgdbImageUrl(game.image));
        }
    }
    const merged = [...assetSources, ...fallbackSources];
    const deduped = Array.from(new Set(merged)).slice(0, 8);
    // eslint-disable-next-line no-console
    console.info('[SuggestedDare] carousel sources', {
        assetCount: (_e = (_d = suggestion.drop.assets) === null || _d === void 0 ? void 0 : _d.length) !== null && _e !== void 0 ? _e : 0,
        similarGamesCount: (_g = (_f = suggestion.drop.similarGames) === null || _f === void 0 ? void 0 : _f.length) !== null && _g !== void 0 ? _g : 0,
        resolved: deduped,
    });
    return deduped;
};
export const SuggestedDareModal = memo(({ suggestion, onAccepted, onSnooze, onDismiss, }) => {
    var _a, _b, _c, _d, _e;
    const modal = useModal();
    const [isSubmitting, setIsSubmitting] = useState(false);
    const { drop, dare, serializedDrop, matchedGenres } = suggestion;
    const { onAcceptDare } = useAcceptDare({ drop, dare });
    const handleAccept = () => __awaiter(void 0, void 0, void 0, function* () {
        setIsSubmitting(true);
        try {
            yield onAcceptDare({
                onSuccess: () => {
                    onAccepted();
                    modal.hide();
                },
            });
        }
        finally {
            setIsSubmitting(false);
        }
    });
    const handleSnooze = () => {
        onSnooze();
        modal.hide();
    };
    const handleDismiss = () => {
        onDismiss(drop.id);
        modal.hide();
    };
    const brandLogo = (_a = serializedDrop.brand) === null || _a === void 0 ? void 0 : _a.img;
    const brandName = (_c = (_b = serializedDrop.brand) === null || _b === void 0 ? void 0 : _b.name) !== null && _c !== void 0 ? _c : '';
    const dropLabel = serializedDrop.label || drop.campaignName;
    const brandWebsite = (_d = drop.brand) === null || _d === void 0 ? void 0 : _d.brandWebsite;
    const dropHref = routeUrls.drop(drop.mnemonicId);
    const dropDaresHref = routeUrls.drop(drop.mnemonicId, DropQueryHash.Dares);
    const displayedGenres = matchedGenres.slice(0, 4);
    const dareShortId = dare.id.slice(0, 8);
    const marqueeText = 'Incoming dare  ·  matched on genre  ·  personalized for you  ·  ';
    const carouselImages = useMemo(() => getCarouselImages(suggestion), [suggestion]);
    const darePlatforms = (_e = dare.platforms) !== null && _e !== void 0 ? _e : [];
    const dareLevel = dare.level;
    const levelLabel = dareLevel ? readableDropDareDifficultyLevels[dareLevel] : null;
    const estimatedTime = dareLevel
        ? dropDareDifficultyEstimatedTime[dareLevel]
        : null;
    const rewardName = useMemo(() => {
        var _a;
        if (!dare.product) {
            return '';
        }
        try {
            return getBountyName({
                amount: dare.amount,
                name: dare.product.name,
                type: dare.product.type,
                region: dare.product.region,
                platform: dare.product.platform,
            });
        }
        catch (_b) {
            return (_a = dare.product.name) !== null && _a !== void 0 ? _a : '';
        }
    }, [dare]);
    return (<ModalLayout className="sd-modal !py-0 !px-0 !gap-0 max-w-2xl w-[95vw] sm:w-[640px] overflow-hidden !rounded-2xl !border-pink">
			<SuggestedDareKeyframes />

			<span className="absolute top-12 sm:top-16 right-3 sm:right-6 rotate-[7deg] bg-teal text-dark-1 px-2.5 sm:px-3 py-0.5 sm:py-1 font-mono text-[10px] sm:text-xs uppercase tracking-widest font-black border-2 border-dark-1 z-[3] shadow-[3px_3px_0_var(--dark-1)] select-none">
				For you
			</span>

			<header className="w-full bg-pink overflow-hidden relative">
				<div className="overflow-hidden py-2.5 pr-14" style={{
            maskImage: 'linear-gradient(to right, transparent 0, black 4%, black 82%, transparent 92%)',
            WebkitMaskImage: 'linear-gradient(to right, transparent 0, black 4%, black 82%, transparent 92%)',
        }}>
					<div className="flex whitespace-nowrap font-mono uppercase tracking-widest font-black text-white text-sm w-max" style={{ animation: 'sd-marquee 22s linear infinite' }}>
						<span>{marqueeText.repeat(4)}</span>
						<span aria-hidden>{marqueeText.repeat(4)}</span>
					</div>
				</div>
				<span aria-hidden className="absolute left-4 top-1/2 -translate-y-1/2 z-[1] pointer-events-none block h-3 w-3 rounded-full bg-teal shadow-[0_0_8px_var(--teal)]">
					<span className="absolute inset-0 rounded-full bg-teal opacity-75 animate-ping"/>
				</span>
			</header>

			<div className="flex flex-col gap-5 sm:gap-7 px-4 sm:px-6 md:px-10 py-7 sm:py-9 w-full text-white relative overflow-hidden">
				<div aria-hidden className="absolute inset-0 pointer-events-none overflow-hidden">
					<div data-sd-anim className="absolute top-0 left-0 w-[420px] h-[420px] rounded-full bg-pink blur-3xl opacity-40 will-change-transform" style={{ animation: 'sd-drift-a 14s ease-in-out infinite alternate' }}/>
					<div data-sd-anim className="absolute bottom-0 right-0 w-[380px] h-[380px] rounded-full bg-teal blur-3xl opacity-30 will-change-transform" style={{ animation: 'sd-drift-b 18s ease-in-out infinite alternate' }}/>
					<div data-sd-anim className="absolute top-1/2 left-1/2 w-[460px] h-[460px] rounded-full bg-primary blur-3xl opacity-45 will-change-transform" style={{ animation: 'sd-drift-c 22s ease-in-out infinite alternate' }}/>
				</div>

				<span data-sd-anim aria-hidden className="absolute top-6 right-6 w-3 h-3 bg-teal rounded-full z-[1]" style={{ animation: 'sd-twinkle 2.8s ease-in-out infinite' }}/>
				<span data-sd-anim aria-hidden className="absolute top-32 left-5 w-2 h-2 bg-pink rounded-full z-[1]" style={{ animation: 'sd-twinkle 3.4s ease-in-out infinite 0.6s' }}/>
				<span data-sd-anim aria-hidden className="absolute top-20 right-14 w-1.5 h-1.5 bg-primary rounded-full z-[1]" style={{ animation: 'sd-twinkle 4s ease-in-out infinite 1.2s' }}/>

				<section className="flex flex-col gap-3 relative z-[2]">
					<span className="font-mono text-xs uppercase tracking-widest font-black text-teal">
						Match · {displayedGenres.length || 1} genre{displayedGenres.length === 1 ? '' : 's'}
					</span>
					<h1 className="font-mono uppercase font-black text-[1.75rem] sm:text-4xl md:text-5xl leading-[0.95] tracking-tight">
						<span className="block text-white">This looks like</span>
						<span className="block bg-gradient-to-r from-pink via-primary to-teal bg-clip-text text-transparent">
							your genre.
						</span>
					</h1>
					<p className="font-mono text-xs sm:text-sm md:text-base text-white max-w-md">
						We think the right Dare unlocks better viewership.
					</p>
				</section>

				<section className="flex flex-col sm:flex-row gap-5 sm:gap-5 md:gap-7 items-stretch sm:items-start relative z-[2]">
					{carouselImages.length > 0 && (<div data-sd-anim className="relative w-36 h-36 sm:w-40 sm:h-40 md:w-52 md:h-52 mx-auto sm:mx-0 flex-shrink-0 border-4 border-pink bg-dark-1 shadow-[6px_6px_0_0_var(--primary)] sm:shadow-[8px_8px_0_0_var(--primary)] overflow-hidden" style={{ animation: 'sd-wiggle 5.5s ease-in-out infinite' }}>
							<AssetCarousel images={carouselImages} alt={dropLabel}/>
						</div>)}
					<div className="flex flex-col gap-2.5 min-w-0 flex-1 w-full">
						<div className="flex items-center gap-2 font-mono text-xs uppercase tracking-wider font-bold text-white min-w-0">
							{brandLogo && (brandWebsite ? (<a href={brandWebsite} target="_blank" rel="noopener noreferrer" title={`${brandName} website`} className="relative w-7 h-7 flex-shrink-0 bg-white rounded p-0.5 hover:opacity-70 transition-opacity">
										<Image src={brandLogo} alt={brandName} layout="fill" objectFit="contain"/>
									</a>) : (<span className="relative w-7 h-7 flex-shrink-0 bg-white rounded p-0.5">
										<Image src={brandLogo} alt={brandName} layout="fill" objectFit="contain"/>
									</span>))}
							{brandName && (brandWebsite ? (<a href={brandWebsite} target="_blank" rel="noopener noreferrer" className="hover:text-teal transition-colors flex-shrink-0">
										{brandName}
									</a>) : <span className="flex-shrink-0">{brandName}</span>)}
							{brandName && <span aria-hidden className="flex-shrink-0 opacity-60">·</span>}
							<Link href={dropHref} legacyBehavior>
								<a className="truncate hover:text-pink transition-colors">{dropLabel}</a>
							</Link>
						</div>
						<h2 className="font-mono uppercase font-black text-base sm:text-xl md:text-2xl leading-[1.1] break-words text-white">
							{dare.title}
						</h2>
						<div className="flex flex-wrap items-center gap-2">
							{darePlatforms.length > 0 && (<div className="flex items-center gap-1.5 px-2.5 py-1 bg-dark-1 border-2 border-teal" title={`Platforms: ${darePlatforms.join(', ')}`}>
									{darePlatforms.map(platform => (<DarePlatformIcon key={platform} size="14" platform={platform} color={PlatformIconColor.Light}/>))}
								</div>)}
							{rewardName && (<div className="flex items-center gap-2 px-2.5 py-1 bg-dark-1 border-2 border-pink max-w-full" title={rewardName}>
									<FaGift className="text-pink" size={14} aria-hidden/>
									<span className="font-mono text-xs font-bold text-white truncate">
										{rewardName}
									</span>
								</div>)}
							{levelLabel && (<div className="flex items-center gap-2 px-2.5 py-1 bg-dark-1 border-2 border-primary" title={`Difficulty: ${levelLabel}`}>
									<FaBolt className="text-primary" size={12} aria-hidden/>
									<span className="font-mono text-xs font-bold text-white uppercase">{levelLabel}</span>
								</div>)}
							{estimatedTime && (<div className="flex items-center gap-2 px-2.5 py-1 bg-dark-1 border-2 border-white/40" title={`Estimated time: ${estimatedTime}`}>
									<FaRegClock className="text-white" size={12} aria-hidden/>
									<span className="font-mono text-xs font-bold text-white">{estimatedTime}</span>
								</div>)}
						</div>
						{dare.description && (<p className="font-mono text-xs sm:text-sm text-white line-clamp-3">
								<FaCaretRight className="inline text-pink mr-2 -translate-y-px" size={12} aria-hidden/>
								{dare.description}
							</p>)}
						<Link href={dropDaresHref} legacyBehavior>
							<a className="inline-flex items-center gap-1.5 w-fit font-mono text-xs uppercase tracking-widest font-black text-teal hover:text-pink transition-colors">
								Explore more dares
								<FaArrowUpRightFromSquare size={11} aria-hidden/>
							</a>
						</Link>
					</div>
				</section>

				{displayedGenres.length > 0 && (<ul className="flex flex-wrap gap-3 relative z-[2]">
						{displayedGenres.map((genre, i) => (<li key={genre} className={`${STAMP_ROTATIONS[i % STAMP_ROTATIONS.length]} font-mono text-xs uppercase tracking-widest font-black bg-pink text-white px-3 py-1 border-2 border-dark-1 select-none shadow-[2px_2px_0_var(--dark-1)]`}>
								{genre}
							</li>))}
					</ul>)}

				<div className="flex flex-col gap-3 pt-1 relative z-[2]">
					<ButtonNew variant={ButtonNewVariant.Primary} onClick={handleAccept} type="button" disabled={isSubmitting} className="w-full !py-4 !rounded-none font-mono uppercase font-black tracking-widest text-base">
						<span className="flex items-center justify-center gap-3">
							<span>{isSubmitting ? 'Accepting' : 'Accept dare'}</span>
							<FaArrowRight data-sd-anim aria-hidden size={18} style={{ animation: 'sd-nudge 1.4s ease-in-out infinite' }}/>
						</span>
					</ButtonNew>
					<div className="flex justify-center items-center gap-4 font-mono text-xs uppercase tracking-wider font-bold text-white">
						<button type="button" onClick={handleSnooze} className="hover:text-teal transition-colors">
							Maybe later
						</button>
						<span className="text-white/50" aria-hidden>·</span>
						<button type="button" onClick={handleDismiss} className="hover:text-pink transition-colors">
							Not for me
						</button>
					</div>
				</div>
			</div>

			<footer className="w-full bg-primary flex justify-between items-center gap-3 px-4 sm:px-8 py-2 sm:py-2.5 font-mono text-[10px] sm:text-xs uppercase tracking-widest font-black text-white">
				<span className="truncate min-w-0">Drop · {drop.mnemonicId}</span>
				<span className="flex-shrink-0">Dare · #{dareShortId}</span>
			</footer>
		</ModalLayout>);
});
